// New sidebar for Crew Management UI - matches Figma design
// Wide sidebar (256px) with brand header, lucide icons, indigo active state, footer
import React, { useMemo, useState, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Users, Activity, Briefcase, ClipboardList, Settings as SettingsIcon, Heart, Stethoscope, Brain, FileCheck, LogOut } from 'lucide-react';
import { signOutUser } from 'auth/authentication';
import { debounce } from 'lodash';
import { cn } from 'lib/utils';
import { Search, Select } from 'components';
import { setUser, setClinicOptions } from 'redux/generalState.actions';
import UserService from 'api/identity/user';
import ClientService from 'api/client/client';

const CrewSidebar = ({ history, location }) => {
  const dispatch = useDispatch();
  const { showSideBar, user, clientInformation, clinicOptions } = useSelector((state) => state.general);
  const activePath = `/${location.pathname.split('/')[1]}`;
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const isAdmin = user?.email?.endsWith('@riverr.ai');
  const isClaimsOnly = user?.metadata?.role === 'claims';

  // Client switcher logic
  const formatAndSetClinicOptions = useCallback((clientGroup) => {
    if (!clientGroup || clientGroup.length === 0) return;
    const options = clientGroup.flatMap((g) =>
      g.clients.map((c) => ({ title: c.name, text: c.name, value: c.clientId }))
    );
    dispatch(setClinicOptions(options));
  }, [dispatch]);

  const switchClient = async (clientId) => {
    if (isAdmin)
      await UserService.adminSwitchClient(clientId, user.id, localStorage.getItem('USER_ID'));
    else await UserService.switchClient(clientId);
    dispatch(setUser({ ...user, currentClientId: clientId }));
    window.location.assign('/workforce');
  };

  const searchClinics = useCallback(
    debounce(async (val) => {
      try {
        setIsSearching(true);
        const { data } = await ClientService.getAllClients({ search: val, limit: 20 });
        formatAndSetClinicOptions([{ clients: [...data.data.clients] }]);
      } catch (e) { /* ignore */ }
      setIsSearching(false);
    }, 750),
    [formatAndSetClinicOptions]
  );
  const isResultsAllowed = user?.email?.includes('@riverr.ai') || user?.email === 'r.dagher@onecaregroup.global';

  const navItems = useMemo(() => {
    const allItems = [
      { id: 'workforce', label: 'Members', icon: Users, path: '/workforce', isHide: isClaimsOnly },
      { id: 'insights', label: 'Health Insights', icon: Activity, path: '/insights', isHide: isClaimsOnly },
      { id: 'wellness', label: 'Wellness KPI', icon: Heart, path: '/wellness', isHide: isClaimsOnly || !clientInformation?.hasWellnessKPI },
      { id: 'encounter', label: 'Encounter', icon: Stethoscope, path: '/encounter', isHide: isClaimsOnly || !clientInformation?.hasMedEncounter },
      { id: 'mental-health', label: 'Mental Health', icon: Brain, path: '/mental-health', isHide: isClaimsOnly || !clientInformation?.hasMentalPEME },
      { id: 'claims', label: 'Claims', icon: Briefcase, path: '/claims' },
      { id: 'results', label: 'Results', icon: ClipboardList, path: '/results', isHide: isClaimsOnly || !isResultsAllowed },
      { id: 'peme-status', label: 'PEME Status', icon: FileCheck, path: '/peme-status', isHide: isClaimsOnly || clientInformation?.hasPEMEStatus === false },
    ];
    return allItems.filter((item) => !item.isHide);
  }, [isClaimsOnly, clientInformation, isResultsAllowed]);

  if (!showSideBar) return null;

  return (
    <div className="tw-w-64 tw-bg-white tw-border-r tw-border-gray-200 tw-h-screen tw-fixed tw-left-0 tw-top-0 tw-flex tw-flex-col tw-z-50">
      {/* Brand header + client switcher */}
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h1 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">Bedrock</h1>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-mb-0">{clientInformation?.name || 'Maritime Management'}</p>
        {user?.metadata?.role !== 'patient' && (
          <div className="tw-mt-3">
            {isAdmin ? (
              <Search
                placeholder="Switch client"
                dropdown={true}
                onSearchChange={(e, { value }) => { setSearch(value); searchClinics(value); }}
                onResultSelect={(e, data) => switchClient(data.result.value)}
                size="small"
                isFilter={true}
                value={search}
                results={clinicOptions}
                isLoading={isSearching}
              />
            ) : clinicOptions?.length > 1 ? (
              <Select
                placeholder="Select clinic"
                value={user?.currentClientId}
                options={clinicOptions}
                onChange={(e, { value }) => switchClient(value)}
                fluid
              />
            ) : null}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="tw-flex-1 tw-p-4">
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.path;

            return (
              <li key={item.id}>
                <button
                  onClick={() => history.push(item.path)}
                  className={cn(
                    'tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer tw-text-left',
                    isActive
                      ? 'tw-bg-indigo-50 tw-text-indigo-700 tw-shadow-sm'
                      : 'tw-text-gray-700 tw-bg-transparent hover:tw-bg-gray-50'
                  )}
                >
                  <Icon
                    className={cn(
                      'tw-w-5 tw-h-5',
                      isActive ? 'tw-text-indigo-600' : 'tw-text-gray-400'
                    )}
                  />
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Settings - bottom pinned */}
      {!isClaimsOnly && (
        <div className="tw-px-4 tw-pb-2">
          <button
            onClick={() => history.push('/settings')}
            className={cn(
              'tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer tw-text-left',
              activePath === '/settings'
                ? 'tw-bg-indigo-50 tw-text-indigo-700 tw-shadow-sm'
                : 'tw-text-gray-700 tw-bg-transparent hover:tw-bg-gray-50'
            )}
          >
            <SettingsIcon
              className={cn(
                'tw-w-5 tw-h-5',
                activePath === '/settings' ? 'tw-text-indigo-600' : 'tw-text-gray-400'
              )}
            />
            Settings
          </button>
        </div>
      )}

      {/* Logout */}
      <div className="tw-px-4 tw-pb-2">
        <button
          onClick={() => signOutUser(true)}
          className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer tw-text-left tw-text-red-500 tw-bg-transparent hover:tw-bg-red-50"
        >
          <LogOut className="tw-w-5 tw-h-5 tw-text-red-400" />
          Logout
        </button>
      </div>

      {/* Footer */}
      <div className="tw-p-4 tw-border-t tw-border-gray-200">
        <p className="tw-text-xs tw-text-gray-500 tw-font-medium tw-mb-0">Version 1.0.0</p>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-mb-0">&copy; 2026 Bedrock</p>
      </div>
    </div>
  );
};

export default withRouter(CrewSidebar);
